// MARK://dhk8/admin-shell/components/InlineEdit
// In-place editable value. Renders the value as a clickable affordance; on click
// it swaps to an input. Enter or blur commits via `await onSave`; Escape cancels.
// Save state is shown with a self-contained ✓ / ✗ marker (NO Toast dependency).
//
// `type` controls the input + display:
//   - 'text'     → plain text
//   - 'number'   → numeric input, locale-grouped display
//   - 'currency' → numeric input, MXN-formatted display
// An optional `format` overrides the display rendering entirely.
//
// Self-contained: React + lucide-react only; scoped `nui-*` CSS.
import { useEffect, useRef, useState } from 'react'
import { Check, X } from 'lucide-react'

type InlineEditType = 'text' | 'number' | 'currency'
type SaveState = 'idle' | 'saving' | 'ok' | 'err'

const INLINE_EDIT_CSS = `
@keyframes nui-ie-spin { to { transform: rotate(360deg); } }
.nui-ie { display: inline-flex; align-items: center; gap: 6px; max-width: 100%; }
.nui-ie__view {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 2px 6px; margin: -2px -6px;
  border-radius: var(--pos-radius-sm, 6px);
  background: transparent; border: 1px solid transparent;
  color: var(--pos-fg); font: inherit; cursor: text; text-align: left;
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  transition: background var(--pos-duration-fast, 120ms) var(--pos-ease-out);
}
.nui-ie__view:hover { background: var(--pos-elevated); }
.nui-ie__view.is-empty { color: var(--pos-fg-tertiary); font-style: italic; }
.nui-ie__input {
  min-height: 34px; padding: 0 var(--pos-space-2, 8px);
  border: 1px solid var(--pos-tint); border-radius: var(--pos-radius-sm, 6px);
  background: var(--pos-canvas); color: var(--pos-fg);
  font-family: var(--font-sans); font-size: var(--pos-text-sm, 13px);
  box-shadow: 0 0 0 3px var(--pos-tint-soft); outline: none;
  max-width: 220px;
}
.nui-ie__spin {
  width: 14px; height: 14px; flex-shrink: 0;
  border: 2px solid var(--pos-separator-strong);
  border-top-color: var(--pos-tint);
  border-radius: 50%;
  animation: nui-ie-spin 0.7s linear infinite;
}
.nui-ie__ok { color: var(--pos-success); display: inline-flex; }
.nui-ie__err { color: var(--pos-danger); display: inline-flex; align-items: center; gap: 4px; }
.nui-ie__err small { font-size: var(--pos-text-xs, 12px); }
`

export interface InlineEditProps {
  value: string | number
  onSave: (next: string) => void | Promise<void>
  type?: InlineEditType
  /** Custom display renderer; overrides the built-in formatting for `type`. */
  format?: (value: string | number) => string
}

const currencyFmt = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
// Hasta 4 decimales (= Decimal(10,4) de cantidades): el default de Intl redondea a 3
// y "se comía" un dígito en la lista (ej. 25.1234 → "25.123") mientras el modal mostraba el crudo.
const numberFmt = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 4 })

function displayValue(value: string | number, type: InlineEditType, format?: InlineEditProps['format']): string {
  if (format) return format(value)
  if (type === 'currency' || type === 'number') {
    const n = typeof value === 'number' ? value : Number(value)
    if (value === '' || value === null || Number.isNaN(n)) return ''
    return type === 'currency' ? currencyFmt.format(n) : numberFmt.format(n)
  }
  return value === null || value === undefined ? '' : String(value)
}

export default function InlineEdit({ value, onSave, type = 'text', format }: InlineEditProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [state, setState] = useState<SaveState>('idle')
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const committedRef = useRef(false)

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  // Auto-clear the transient ✓ marker.
  useEffect(() => {
    if (state !== 'ok') return
    const t = setTimeout(() => setState('idle'), 1400)
    return () => clearTimeout(t)
  }, [state])

  function begin() {
    setErrMsg(null)
    setState('idle')
    setDraft(value === null || value === undefined ? '' : String(value))
    setEditing(true)
  }

  async function commit() {
    if (committedRef.current) return
    committedRef.current = true
    const next = draft.trim()
    setEditing(false)
    if (next === String(value ?? '')) { setState('idle'); return }
    setState('saving')
    try {
      await onSave(next)
      setState('ok')
      setErrMsg(null)
    } catch (e) {
      setState('err')
      setErrMsg(e instanceof Error ? e.message : 'Error al guardar')
    }
  }

  function cancel() {
    committedRef.current = true
    setEditing(false)
    setState('idle')
  }

  const display = displayValue(value, type, format)

  return (
    <span className="nui-ie">
      <style>{INLINE_EDIT_CSS}</style>

      {editing ? (
        <input
          ref={inputRef}
          className="nui-ie__input"
          type={type === 'text' ? 'text' : 'number'}
          inputMode={type === 'text' ? undefined : 'decimal'}
          step={type === 'currency' ? '0.01' : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => { committedRef.current = false }}
          onBlur={() => { void commit() }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void commit() }
            else if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
        />
      ) : (
        <button type="button" className={`nui-ie__view${display ? '' : ' is-empty'}`} onClick={begin}>
          {display || 'Añadir…'}
        </button>
      )}

      {state === 'saving' && <span className="nui-ie__spin" role="status" aria-label="Guardando" />}
      {state === 'ok' && <span className="nui-ie__ok" aria-label="Guardado"><Check size={15} aria-hidden /></span>}
      {state === 'err' && (
        <span className="nui-ie__err" role="alert">
          <X size={15} aria-hidden />
          {errMsg ? <small>{errMsg}</small> : null}
        </span>
      )}
    </span>
  )
}
